/* OptimizedImage (audit 2026-07, perf item #6)
 *
 * Drop-in replacement for raw <img> that routes through next/image — AVIF/WebP,
 * per-device resizing, lazy loading — WITHOUT the classic next/image footgun:
 * a src whose host isn't in next.config.js images.remotePatterns makes
 * /_next/image return 400 and the photo silently breaks. Masjid photos come
 * from the database and can live on any host, so this component optimizes
 * relative URLs and known-allowed hosts, and falls back to a plain <img> for
 * anything else. A photo can therefore never regress from this migration —
 * worst case it renders exactly as before.
 *
 * KEEP ALLOWED_HOST_SUFFIXES IN SYNC with images.remotePatterns in
 * next.config.js. next/image renders a real <img>, so existing CSS selectors
 * like `.heroMedia img { … }` keep working unchanged.
 */
import Image from 'next/image'
import type { CSSProperties } from 'react'

const ALLOWED_HOST_SUFFIXES = [
  '.railway.app',
  '.amazonaws.com',
  '.masjids.in',
  '.cloudinary.com',
  '.unsplash.com',
  'images.unsplash.com',
  'upload.wikimedia.org',
  '.wikipedia.org',
]

function isOptimizable(src: string): boolean {
  if (!src) return false
  if (src.startsWith('/') && !src.startsWith('//')) return true // local /public or same-origin
  try {
    const { protocol, hostname } = new URL(src)
    if (protocol !== 'https:') return false
    return ALLOWED_HOST_SUFFIXES.some(
      (s) => (s.startsWith('.') ? hostname.endsWith(s) || hostname === s.slice(1) : hostname === s)
    )
  } catch {
    return false
  }
}

interface Props {
  src: string
  alt: string
  /** Fill the nearest positioned ancestor (hero covers, card media). */
  fill?: boolean
  width?: number
  height?: number
  /** Required with fill for correct srcset selection, e.g. "100vw" or "(max-width:768px) 100vw, 33vw". */
  sizes?: string
  /** Above-the-fold LCP images only — preloads and disables lazy loading. */
  priority?: boolean
  className?: string
  style?: CSSProperties
}

export default function OptimizedImage({
  src, alt, fill, width, height, sizes, priority, className, style,
}: Props) {
  if (isOptimizable(src)) {
    return (
      <Image
        src={src}
        alt={alt}
        fill={fill}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        sizes={sizes}
        priority={priority}
        className={className}
        style={style}
      />
    )
  }
  // Off-list host: render exactly what was there before this migration.
  // eslint-disable-next-line @next/next/no-img-element
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      className={className}
      style={style}
    />
  )
}
